import * as THREE from 'three';
import { Spin } from '../animated';
import { CLASSICAL, INDUSTRIAL, MODERN } from '../materials';
import { HexPad, levelEntry, type ModelProps, pyramid } from '../parts';
import { FenceSection } from './fence';

const { SOIL, WHEAT, RIPE_WHEAT, CABBAGE, BARN, DARK_WOOD, HAY, STONE, PLASTER, WATER, WOOL } =
  CLASSICAL;
const { TRACTOR, TYRE, STEEL, SOOT } = INDUSTRIAL;
const { CONCRETE, GLASS, GREEN_LEAF } = MODERN;

const G = {
  plot: new THREE.BoxGeometry(0.62, 0.03, 0.72),
  cropRow: new THREE.BoxGeometry(0.56, 0.06, 0.06),
  barn: new THREE.BoxGeometry(0.22, 0.18, 0.26),
  barnRoof: pyramid(0.2, 0.12),
  fieldRow: new THREE.BoxGeometry(1, 0.06, 0.06),
  bale: new THREE.CylinderGeometry(0.045, 0.045, 0.08, 8).rotateZ(Math.PI / 2),
  haystack: new THREE.ConeGeometry(0.11, 0.2, 7),
  millTower: new THREE.CylinderGeometry(0.035, 0.055, 0.44, 6),
  millCap: new THREE.ConeGeometry(0.06, 0.08, 6),
  sail: new THREE.BoxGeometry(0.035, 0.17, 0.008),
  hub: new THREE.SphereGeometry(0.022, 6, 5),
  silo: new THREE.CylinderGeometry(0.075, 0.075, 0.38, 10),
  siloDome: new THREE.SphereGeometry(0.075, 10, 6, 0, Math.PI * 2, 0, Math.PI / 2),
  sheep: new THREE.BoxGeometry(0.07, 0.05, 0.045),
  sheepHead: new THREE.BoxGeometry(0.025, 0.03, 0.03),
  ditch: new THREE.BoxGeometry(1, 0.02, 0.1),
  // L4 tractor (faces +x): body, cab, big rear and small front wheels, exhaust.
  tractorBody: new THREE.BoxGeometry(0.12, 0.05, 0.07),
  tractorCab: new THREE.BoxGeometry(0.06, 0.07, 0.07),
  rearWheel: new THREE.CylinderGeometry(0.038, 0.038, 0.025, 10).rotateX(Math.PI / 2),
  frontWheel: new THREE.CylinderGeometry(0.022, 0.022, 0.02, 8).rotateX(Math.PI / 2),
  exhaust: new THREE.CylinderGeometry(0.006, 0.006, 0.06, 5),
  // L5 hydroponics: glass racks in place of crop rows, and a vertical farm tower.
  rack: new THREE.BoxGeometry(0.56, 0.07, 0.07),
  rackStrip: new THREE.BoxGeometry(0.56, 0.012, 0.075),
  rackGreens: new THREE.BoxGeometry(0.54, 0.02, 0.05),
  vfTower: new THREE.BoxGeometry(0.15, 0.5, 0.15),
  vfFloor: new THREE.BoxGeometry(0.156, 0.03, 0.156),
  vfStrip: new THREE.BoxGeometry(0.156, 0.012, 0.156),
  vfCap: new THREE.BoxGeometry(0.12, 0.03, 0.12),
  house: new THREE.BoxGeometry(0.2, 0.13, 0.14),
  // A gable roof along x: a triangular prism, apex up.
  houseRoof: new THREE.CylinderGeometry(0.1, 0.1, 0.22, 3)
    .rotateZ(Math.PI / 2)
    .rotateX(-Math.PI / 2),
};

/** What a farm shows at each level; a level past the end uses the last entry. */
type FarmLevel = {
  /** Barn scale (grown from its base). */
  barn: number;
  /** Crop row materials, cycled front to back. */
  crops: THREE.Material[];
  /** Crop row height scale. */
  cropHeight: number;
  windmill: boolean;
  hay: boolean;
  /** Grain silos with team domes (0–2). */
  silos: number;
  siloScale: number;
  pen: boolean;
  /** Farmhouse with a team roof behind the plot. */
  house: boolean;
  /** A tractor parked beside the barn. */
  tractor: boolean;
  /** Glass hydroponic racks with glowing grow strips on a concrete bed, in place of crop rows. */
  hydroponic: boolean;
  /** A glass vertical-farm tower with grow floors and glow strips, in place of the windmill. */
  vertical: boolean;
};

// Index = level - 1; each entry builds on the previous one. Later eras add one entry each.
const FARM_LEVELS: FarmLevel[] = [
  // L1: plot with crop rows and a small barn.
  {
    barn: 1,
    crops: [WHEAT],
    cropHeight: 1,
    windmill: false,
    hay: false,
    silos: 0,
    siloScale: 1,
    pen: false,
    house: false,
    tractor: false,
    hydroponic: false,
    vertical: false,
  },
  // L2: a larger barn, a windmill and hay bales.
  {
    barn: 1.3,
    crops: [WHEAT],
    cropHeight: 1,
    windmill: true,
    hay: true,
    silos: 0,
    siloScale: 1,
    pen: false,
    house: false,
    tractor: false,
    hydroponic: false,
    vertical: false,
  },
  // L3: ripe amber rows alternating with green cabbage, a grain silo with a team-coloured dome, a sheep pen and a farmhouse.
  {
    barn: 1.3,
    crops: [RIPE_WHEAT, CABBAGE],
    cropHeight: 1,
    windmill: true,
    hay: true,
    silos: 1,
    siloScale: 1,
    pen: true,
    house: true,
    tractor: false,
    hydroponic: false,
    vertical: false,
  },
  // L4 (Industrial) mechanised: taller rows, a tractor, and two bigger silos.
  {
    barn: 1.3,
    crops: [RIPE_WHEAT, CABBAGE],
    cropHeight: 1.2,
    windmill: true,
    hay: true,
    silos: 2,
    siloScale: 1.3,
    pen: true,
    house: true,
    tractor: true,
    hydroponic: false,
    vertical: false,
  },
  // L5 (Modern) hydroponic: glass racks with glowing strips and a vertical-farm tower.
  {
    barn: 1.3,
    crops: [GREEN_LEAF],
    cropHeight: 1,
    windmill: false,
    hay: false,
    silos: 2,
    siloScale: 1.3,
    pen: true,
    house: true,
    tractor: true,
    hydroponic: true,
    vertical: true,
  },
];

function farmLevel(level: number): FarmLevel {
  return levelEntry(FARM_LEVELS, level);
}

const CROP_ROWS = [-0.26, -0.13, 0, 0.13, 0.26];

// Hay bales lying on the ground behind the plot: two side by side, one on top.
const BALES: [number, number, number][] = [
  [-0.24, 0.045, -0.47],
  [-0.14, 0.045, -0.47],
  [-0.19, 0.12, -0.47],
];

const SAIL_ANGLES = [0, Math.PI / 2, Math.PI, (Math.PI * 3) / 2];
const SAIL_SPEED = 0.8; // radians per second

/** A slim windmill whose four sails turn slowly, facing the camera (+z). */
function Windmill() {
  return (
    // Back left, behind the low crop rows, so its silhouette stands clear of the barn.
    <group position={[-0.4, 0, -0.44]}>
      <mesh geometry={G.millTower} material={STONE} position={[0, 0.22, 0]} />
      <mesh geometry={G.millCap} material={DARK_WOOD} position={[0, 0.48, 0]} />
      <Spin axis="z" speed={SAIL_SPEED} position={[0, 0.42, 0.06]}>
        <mesh geometry={G.hub} material={DARK_WOOD} />
        {SAIL_ANGLES.map((a) => (
          <group key={a} rotation={[0, 0, a]}>
            <mesh geometry={G.sail} material={PLASTER} position={[0, 0.1, 0]} />
          </group>
        ))}
      </Spin>
    </group>
  );
}

/** A red tractor facing +x. */
function Tractor({ position, yaw = 0 }: { position: [number, number, number]; yaw?: number }) {
  return (
    <group position={position} rotation={[0, yaw, 0]}>
      <mesh geometry={G.tractorBody} material={TRACTOR} position={[0.02, 0.055, 0]} />
      <mesh geometry={G.tractorCab} material={TRACTOR} position={[-0.03, 0.1, 0]} />
      <mesh geometry={G.exhaust} material={SOOT} position={[0.05, 0.11, 0.02]} />
      {[-0.045, 0.045].map((z) => (
        <group key={z}>
          <mesh geometry={G.rearWheel} material={TYRE} position={[-0.035, 0.038, z]} />
          <mesh geometry={G.frontWheel} material={TYRE} position={[0.06, 0.022, z * 0.9]} />
        </group>
      ))}
    </group>
  );
}

// Behind the barn's right (the first silo stands at its front right corner).
const SILOS: [number, number, number][] = [
  [0.56, 0, 0.22],
  [0.5, 0, -0.36],
];
const TRACTOR_SPOT: [number, number, number] = [0.38, 0, 0.42];
const VERTICAL_FLOORS = [0.1, 0.22, 0.34];

/** A glass vertical-farm tower (back left, where the windmill stood) with grow floors and glow strips. */
function VerticalFarm({ accent, glow }: { accent: THREE.Material; glow: THREE.Material }) {
  return (
    <group position={[-0.4, 0, -0.44]}>
      <mesh geometry={G.vfTower} material={GLASS} position={[0, 0.25, 0]} />
      {VERTICAL_FLOORS.map((y) => (
        <group key={y}>
          <mesh geometry={G.vfFloor} material={GREEN_LEAF} position={[0, y, 0]} />
          <mesh geometry={G.vfStrip} material={glow} position={[0, y + 0.03, 0]} />
        </group>
      ))}
      <mesh geometry={G.vfCap} material={accent} position={[0, 0.515, 0]} />
    </group>
  );
}

// Pen in front of the plot: centre, size, and three sheep inside.
const PEN = { x: -0.06, z: 0.48, w: 0.42, d: 0.14 };
const SHEEP = [
  { x: -0.17, z: 0.47, yaw: 0.3 },
  { x: -0.05, z: 0.5, yaw: -0.4 },
  { x: 0.07, z: 0.46, yaw: 0.2 },
];
const SHEEP_SCALE = 1.6;
// Low rails (top under 0.05) so the sheep show over them.
const PEN_FENCE_HEIGHT = 0.4;

/** A small fenced pen with three sheep. */
function SheepPen() {
  const { x, z, w, d } = PEN;
  return (
    <>
      <FenceSection x={x} z={z - d / 2} yaw={0} length={w} height={PEN_FENCE_HEIGHT} />
      <FenceSection x={x} z={z + d / 2} yaw={0} length={w} height={PEN_FENCE_HEIGHT} />
      <FenceSection x={x - w / 2} z={z} yaw={Math.PI / 2} length={d} height={PEN_FENCE_HEIGHT} />
      <FenceSection x={x + w / 2} z={z} yaw={Math.PI / 2} length={d} height={PEN_FENCE_HEIGHT} />
      {SHEEP.map((s) => (
        <group key={s.x} position={[s.x, 0, s.z]} rotation={[0, s.yaw, 0]} scale={SHEEP_SCALE}>
          <mesh geometry={G.sheep} material={WOOL} position={[0, 0.04, 0]} />
          <mesh geometry={G.sheepHead} material={DARK_WOOD} position={[0.045, 0.055, 0]} />
        </group>
      ))}
    </>
  );
}

/** Crop rows beside a barn with a team-coloured roof; each level adds to the farmstead. */
export function Farm({ accent, glow, level }: ModelProps) {
  const l = farmLevel(level);
  return (
    <>
      <mesh
        geometry={G.plot}
        material={l.hydroponic ? CONCRETE : SOIL}
        position={[-0.12, 0.015, 0]}
      />
      {l.hydroponic &&
        CROP_ROWS.map((z) => (
          <group key={z} position={[-0.12, 0.03, z]}>
            <mesh geometry={G.rack} material={GLASS} position={[0, 0.035, 0]} />
            <mesh geometry={G.rackGreens} material={GREEN_LEAF} position={[0, 0.08, 0]} />
            <mesh geometry={G.rackStrip} material={glow} position={[0, 0.02, 0]} />
          </group>
        ))}
      {!l.hydroponic &&
        CROP_ROWS.map((z, i) => (
          <mesh
            key={z}
            geometry={G.cropRow}
            material={l.crops[i % l.crops.length]}
            position={[-0.12, 0.03 + 0.03 * l.cropHeight, z]}
            scale={[1, l.cropHeight, 1]}
          />
        ))}
      <group position={[0.34, 0, -0.05]} scale={l.barn}>
        <mesh geometry={G.barn} material={BARN} position={[0, 0.09, 0]} />
        <mesh geometry={G.barnRoof} material={accent} position={[0, 0.24, 0]} />
      </group>
      {l.windmill && <Windmill />}
      {l.vertical && <VerticalFarm accent={accent} glow={glow} />}
      {l.tractor && <Tractor position={TRACTOR_SPOT} yaw={-0.5} />}
      {l.hay &&
        BALES.map((p) => (
          <mesh key={`${p[0]}:${p[1]}`} geometry={G.bale} material={HAY} position={p} />
        ))}
      {SILOS.slice(0, l.silos).map((p) => (
        <group key={p[2]} position={p} scale={[l.siloScale, l.siloScale, l.siloScale]}>
          <mesh geometry={G.silo} material={l.silos > 1 ? STEEL : STONE} position={[0, 0.19, 0]} />
          <mesh geometry={G.siloDome} material={accent} position={[0, 0.38, 0]} />
        </group>
      ))}
      {l.pen && <SheepPen />}
      {l.house && (
        <group position={[0.06, 0, -0.49]}>
          <mesh geometry={G.house} material={PLASTER} position={[0, 0.065, 0]} />
          <mesh geometry={G.houseRoof} material={accent} position={[0, 0.18, 0]} />
        </group>
      )}
    </>
  );
}

/** A crop row across a field tile: its z, length and centre x (0 unless trimmed). */
type FieldRow = { z: number; length: number; x?: number };

// Crop rows across a whole grown field tile at these z offsets, each as long as fits the hex.
function fieldRows(zs: number[]): FieldRow[] {
  return zs.map((z) => ({ z, length: 1.1 - Math.abs(z) * 0.9 }));
}

/** What a grown farm's extra tile shows at each level; a level past the end uses the last entry. */
type FarmTileLevel = {
  rows: FieldRow[];
  crop: THREE.Material;
  cropHeight: number;
  /** Row depth scale: thinner rows keep soil visible between dense ones. */
  rowDepth: number;
  bales: [number, number, number][];
  ditch: boolean;
  haystack: boolean;
  /** A dirt lane down the middle (in place of the ditch) with a tractor on it. */
  tractor: boolean;
  /** Glass hydroponic racks with glow strips on concrete, in place of crop rows. */
  hydroponic: boolean;
};

const SPARSE_ROWS = fieldRows([-0.44, -0.3, -0.15, 0, 0.15, 0.3, 0.44]);
// L2: the middle row left fallow for the hay bales.
const FALLOW_ROWS = fieldRows([-0.44, -0.3, -0.15, 0.15, 0.3, 0.44]);
// The haystack's clear spot on the dense rows: rows at these z start right of this x.
const HAYSTACK: [number, number, number] = [-0.3, 0.13, 0.25];
const HAYSTACK_ROWS = [0.2, 0.3];
const HAYSTACK_CLEAR_X = -0.17;
// Denser rows with a gap down the middle for the irrigation ditch, trimmed round the haystack.
const DENSE_ROWS = fieldRows([-0.5, -0.4, -0.3, -0.2, -0.1, 0.1, 0.2, 0.3, 0.4, 0.5]).map((row) => {
  if (!HAYSTACK_ROWS.includes(row.z)) return row;
  const right = row.length / 2;
  return { z: row.z, length: right - HAYSTACK_CLEAR_X, x: (right + HAYSTACK_CLEAR_X) / 2 };
});
// Hay bales on the fallow middle strip (L2), then lying on the taller rows (L3).
const FALLOW_BALES: [number, number, number][] = [
  [-0.32, 0.075, 0],
  [-0.06, 0.075, 0],
  [0.2, 0.075, 0],
  [0.29, 0.075, 0],
];
const ROW_BALES: [number, number, number][] = [
  [0.1, 0.147, 0.2],
  [0.2, 0.147, 0.2],
  [0.3, 0.147, -0.2],
  [-0.1, 0.147, -0.3],
];

// Index = level - 1 (extra tiles only exist from L2). Later eras add one entry each.
const FARM_TILE_LEVELS: FarmTileLevel[] = [
  // L1 (never grown; kept so the table indexes by level): plain crop rows.
  {
    rows: SPARSE_ROWS,
    crop: WHEAT,
    cropHeight: 1,
    rowDepth: 1,
    bales: [],
    ditch: false,
    haystack: false,
    tractor: false,
    hydroponic: false,
  },
  // L2: crop rows and a few hay bales.
  {
    rows: FALLOW_ROWS,
    crop: WHEAT,
    cropHeight: 1,
    rowDepth: 1,
    bales: FALLOW_BALES,
    ditch: false,
    haystack: false,
    tractor: false,
    hydroponic: false,
  },
  // L3: denser, taller amber rows split by an irrigation ditch, plus a haystack.
  {
    rows: DENSE_ROWS,
    crop: RIPE_WHEAT,
    cropHeight: 1.2,
    rowDepth: 0.67,
    bales: ROW_BALES,
    ditch: true,
    haystack: true,
    tractor: false,
    hydroponic: false,
  },
  // L4: the dense rows taller still, the ditch a dirt lane with a tractor on it.
  {
    rows: DENSE_ROWS,
    crop: RIPE_WHEAT,
    cropHeight: 1.4,
    rowDepth: 0.67,
    bales: ROW_BALES,
    ditch: false,
    haystack: true,
    tractor: true,
    hydroponic: false,
  },
  // L5: glass hydroponic racks with glowing strips across a concrete bed.
  {
    rows: SPARSE_ROWS,
    crop: GREEN_LEAF,
    cropHeight: 1,
    rowDepth: 1,
    bales: [],
    ditch: false,
    haystack: false,
    tractor: false,
    hydroponic: true,
  },
];

function farmTileLevel(level: number): FarmTileLevel {
  return levelEntry(FARM_TILE_LEVELS, level);
}

// The L4 tile's lane down the middle gap of the dense rows, and the tractor on it.
const LANE_TRACTOR: [number, number, number] = [0.18, 0.03, 0];

/** A grown farm's extra tile: crop rows across the whole hex, richer with each level. */
export function FarmTile({ glow, level }: ModelProps) {
  const l = farmTileLevel(level);
  if (l.hydroponic) {
    return (
      <>
        <HexPad material={CONCRETE} />
        {l.rows.map((row) => (
          <group key={row.z} position={[0, 0.03, row.z]} scale={[row.length / 0.56, 1, 1]}>
            <mesh geometry={G.rack} material={GLASS} position={[0, 0.035, 0]} />
            <mesh geometry={G.rackGreens} material={l.crop} position={[0, 0.08, 0]} />
            <mesh geometry={G.rackStrip} material={glow} position={[0, 0.02, 0]} />
          </group>
        ))}
      </>
    );
  }
  return (
    <>
      <HexPad material={SOIL} />
      {l.ditch && <mesh geometry={G.ditch} material={WATER} position={[0, 0.04, 0]} />}
      {l.tractor && <Tractor position={LANE_TRACTOR} />}
      {l.rows.map((row) => (
        <mesh
          key={row.z}
          geometry={G.fieldRow}
          material={l.crop}
          position={[row.x ?? 0, 0.03 + 0.03 * l.cropHeight, row.z]}
          scale={[row.length, l.cropHeight, l.rowDepth]}
        />
      ))}
      {l.bales.map((p) => (
        <mesh key={`${p[0]}:${p[2]}`} geometry={G.bale} material={HAY} position={p} />
      ))}
      {l.haystack && <mesh geometry={G.haystack} material={HAY} position={HAYSTACK} />}
    </>
  );
}
